import React from 'react';
import { formatNotificationText } from '../../../utils/notificationsService';
import './NotificationMessage.css';

/**
 * Komponent renderujący tekst powiadomienia z klikalnymi linkami
 */
function NotificationMessage({ notification, t, onOpenProfile, onOpenEvent }) {
  const { type, data, event } = notification;

  // Helper do renderowania klikalnego tekstu
  const renderClickableText = (text, onClick, modifierClass = '') => {
    const className = ['notification-link', modifierClass].filter(Boolean).join(' ');
    return (
      <span
        className={className}
        onClick={(e) => {
          e.stopPropagation(); // Zapobiegaj kliknięciu w całe powiadomienie
          onClick();
        }}
        role="button"
        tabIndex={0}
      >
        {text}
      </span>
    );
  };

  switch (type) {
    case 'application_received':
    case 'application_accepted':
    case 'application_rejected':
    case 'event_created':
    case 'event_starts_soon':
    case 'event_started':
    case 'event_cancelled':
    case 'event_invitation':
    case 'participant_kicked':
    case 'friend_joined_event': {
      const eventTitle = event?.title || data?.event_title || t('notifications.unknown_event');
      const eventId = notification.event_id || event?.id;
      
      if (type === 'application_received') {
        const applicantId = data?.applicant_id;
        // Używamy tłumaczeń, ale z klikalnymi linkami
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        if (applicantId) {
          return (
            <>
              {t('notifications.types.application_received.beforeEvent', 'Do wydarzenia ')}
              {eventLink}
              {t('notifications.types.application_received.afterEvent', ' zapisał się ')}
              {renderClickableText(
                t('notifications.types.application_received.applicant', 'nowy użytkownik'),
                () => onOpenProfile(applicantId),
                'notification-link-user'
              )}
              {t('notifications.types.application_received.end', ' i czeka na akceptację')}
            </>
          );
        }
        // Fallback jeśli nie ma applicantId
        return (
          <>
            {t('notifications.types.application_received.beforeEvent', 'Do wydarzenia ')}
            {eventLink}
            {t('notifications.types.application_received.afterEvent', ' zapisał się ')}
            {t('notifications.types.application_received.applicant', 'nowy użytkownik')}
            {t('notifications.types.application_received.end', ' i czeka na akceptację')}
          </>
        );
      } else if (type === 'application_accepted') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.application_accepted.before', 'Twoje zgłoszenie do wydarzenia ')}
            {eventLink}
            {t('notifications.types.application_accepted.after', ' zostało zaakceptowane')}
          </>
        );
      } else if (type === 'application_rejected') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.application_rejected.before', 'Twoje zgłoszenie do wydarzenia ')}
            {eventLink}
            {t('notifications.types.application_rejected.after', ' zostało odrzucone')}
          </>
        );
      } else if (type === 'event_created') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.event_created.before', 'Utworzono nowe wydarzenie ')}
            {eventLink}
          </>
        );
      } else if (type === 'event_starts_soon') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.event_starts_soon.before', 'Wydarzenie ')}
            {eventLink}
            {t('notifications.types.event_starts_soon.after', ' zaczyna się wkrótce')}
          </>
        );
      } else if (type === 'event_started') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;

        return (
          <>
            {t('notifications.types.event_started.before', 'Wydarzenie ')}
            {eventLink}
            {t('notifications.types.event_started.after', ' właśnie się rozpoczęło')}
          </>
        );
      } else if (type === 'friend_joined_event') {
        const friendId = data?.friend_id;
        const friendName = data?.friend_name || t('notifications.unknown_user');
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {friendId ? renderClickableText(
              friendName,
              () => onOpenProfile(friendId),
              'notification-link-user'
            ) : friendName}
            {t('notifications.types.friend_joined_event.beforeEvent', ' dołączył do wydarzenia ')}
            {eventLink}
          </>
        );
      } else if (type === 'event_invitation') {
        const inviterName = data?.inviter_name || t('notifications.unknown_user');
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {inviterName}
            {t('notifications.types.event_invitation.after', ' wysyła Ci zaproszenie do wydarzenia ')}
            {eventLink}
          </>
        );
      } else if (type === 'participant_kicked') {
        const kickerName = data?.kicker_name || t('notifications.unknown_user');
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.participant_kicked.before', 'Zostałeś wyrzucony z wydarzenia ')}
            {eventLink}
            {t('notifications.types.participant_kicked.after', ' przez ')}
            {kickerName}
          </>
        );
      } else if (type === 'event_cancelled') {
        const eventLink = eventId ? renderClickableText(
          `"${eventTitle}"`,
          () => onOpenEvent(eventId),
          'notification-link-event'
        ) : `"${eventTitle}"`;
        
        return (
          <>
            {t('notifications.types.event_cancelled.before', 'Wydarzenie ')}
            {eventLink}
            {t('notifications.types.event_cancelled.after', ' zostało anulowane przez organizatora')}
          </>
        );
      }
      return <>{formatNotificationText(notification, t)}</>;
    }

    case 'friend_invitation': {
      const senderId = data?.sender_id;
      const senderName = data?.sender_name || t('notifications.unknown_user');
      
      return (
        <>
          {senderId ? renderClickableText(
            senderName,
            () => onOpenProfile(senderId),
            'notification-link-user'
          ) : senderName}
          {t('notifications.types.friend_invitation.after', ' wysłał Ci zaproszenie do znajomych')}
        </>
      );
    }

    case 'friendship_accepted': {
      const friendId = data?.friend_id;
      const friendName = data?.friend_name || t('notifications.unknown_user');
      
      return (
        <>
          {t('notifications.types.friendship_accepted.before', 'Ty i ')}
          {friendId ? renderClickableText(
            friendName,
            () => onOpenProfile(friendId),
            'notification-link-user'
          ) : friendName}
          {t('notifications.types.friendship_accepted.after', ' jesteście teraz znajomymi 🎉')}
        </>
      );
    }

    default:
      return <>{formatNotificationText(notification, t)}</>;
  }
}

export default NotificationMessage;
